// src/utils/phaseMapping.js
// Foundational utilities for the three-phase lifecycle: Acquisition → Rehab → Selling

// ─── Phase stage definitions ──────────────────────────────────────────────────

export const PHASE_STAGES = {
  acquisition: [
    'identified',
    'underwriting',
    'inspection',
    'final_underwriting',
    'pending_purchase',
  ],
  rehab: ['planning', 'permits', 'in_progress', 'punch_list', 'rehab_complete'],
  selling: ['pre_listing', 'on_market', 'under_contract', 'closing', 'sold'],
};

// ─── Backward compatibility: legacy status → phase mapping ────────────────────
// Existing projects may not have a `phase` field. This maps old statuses to phases.

const LEGACY_STATUS_TO_PHASE = {
  onboarding: { phase: 'rehab', status: 'planning' },
  rehab: { phase: 'rehab', status: 'in_progress' },
  rehabilitating: { phase: 'rehab', status: 'in_progress' },
  active: { phase: 'selling', status: 'on_market' },
  selling: { phase: 'selling', status: 'on_market' },
  undercontract: { phase: 'selling', status: 'under_contract' },
  sold: { phase: 'selling', status: 'sold' },
};

/**
 * Given a legacy status string, return { phase, status } for the new lifecycle.
 * Returns null if the status is already a new-style status.
 */
export function getPhaseFromLegacyStatus(status) {
  return LEGACY_STATUS_TO_PHASE[status] || null;
}

/**
 * Resolve the phase for a project. Checks the explicit `phase` field first,
 * then falls back to inferring from the legacy `status` field.
 */
export function resolveProjectPhase(project) {
  if (!project) return 'rehab';
  if (project.phase) return project.phase;

  const legacy = LEGACY_STATUS_TO_PHASE[project.status];
  return legacy ? legacy.phase : 'rehab';
}

/**
 * Resolve the normalized status for a project within its phase.
 */
export function resolveProjectStatus(project) {
  if (!project) return 'planning';

  // If project already has a phase field, trust its status
  if (project.phase) return project.status;

  // Otherwise, map legacy status to new status
  const legacy = LEGACY_STATUS_TO_PHASE[project.status];
  return legacy ? legacy.status : project.status;
}

// ─── Label helpers ────────────────────────────────────────────────────────────

const PHASE_LABELS = {
  acquisition: 'Acquisition',
  rehab: 'Rehab',
  selling: 'Selling',
};

const STAGE_LABELS = {
  // Acquisition stages (pipeline)
  identified: 'Initial Analysis',
  underwriting: 'Offer',
  inspection: 'Inspection',
  final_underwriting: 'Final Underwriting',
  pending_purchase: 'Pending Purchase',
  acquired: 'Acquired',
  // Approval pipeline (enterprise — kept for backward compat)
  cfo_review: 'Deal Review',
  ceo_approval: 'Final Approval',
  funding_confirmed: 'Funding Secured',
  // Rehab stages
  planning: 'Planning',
  permits: 'Permits',
  in_progress: 'In Progress',
  punch_list: 'Punch List',
  rehab_complete: 'Complete',
  // Selling stages
  pre_listing: 'Pre-Listing',
  on_market: 'On Market',
  under_contract: 'Under Contract',
  closing: 'Closing',
  sold: 'Sold',
};

export function getPhaseLabel(phase) {
  return PHASE_LABELS[phase] || phase;
}

export function getStageLabel(status) {
  return STAGE_LABELS[status] || status;
}

// Journey labels for the Lead Detail header / pipeline. These mirror the
// stage names along the acquisition journey (Underwriting → Inspection → ...)
// rather than the Flip Flow Kanban labels (where `underwriting` is "Offer").
// Use this anywhere the user sees the "current stage" of a single lead's
// journey, so the header badge and LeadStagePipeline agree.
const STAGE_JOURNEY_LABELS = {
  identified: 'Identified',
  underwriting: 'Underwriting',
};

export function getStageJourneyLabel(status) {
  return STAGE_JOURNEY_LABELS[status] || getStageLabel(status);
}

// ─── Color helpers ────────────────────────────────────────────────────────────

const STAGE_COLORS = {
  // Acquisition stages
  identified: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-700 dark:text-violet-300',
    border: 'border-violet-300 dark:border-violet-700',
  },
  underwriting: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
  },
  inspection: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  final_underwriting: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
  },
  pending_purchase: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
  },
  acquired: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-300 dark:border-green-700',
  },
  // Approval pipeline (enterprise — kept for backward compat)
  cfo_review: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  ceo_approval: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
  },
  funding_confirmed: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-300 dark:border-cyan-700',
  },
  // Rehab stages
  planning: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-300 dark:border-slate-600',
  },
  permits: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  in_progress: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
  },
  punch_list: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
  },
  rehab_complete: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
  },
  // Selling stages
  pre_listing: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-300 dark:border-purple-700',
  },
  on_market: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
  },
  under_contract: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-300 dark:border-cyan-700',
  },
  closing: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  sold: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
  },
};

const DEFAULT_COLOR = {
  bg: 'bg-gray-100 dark:bg-gray-800',
  text: 'text-gray-700 dark:text-gray-300',
  border: 'border-gray-300 dark:border-gray-600',
};

export function getStageColor(status) {
  return STAGE_COLORS[status] || DEFAULT_COLOR;
}

// ─── Phase column definitions (for pipeline boards) ──────────────────────────

export const REHAB_COLUMNS = [
  { key: 'planning', label: 'Planning', color: STAGE_COLORS.planning },
  { key: 'permits', label: 'Permits', color: STAGE_COLORS.permits },
  { key: 'in_progress', label: 'In Progress', color: STAGE_COLORS.in_progress },
  { key: 'punch_list', label: 'Punch List', color: STAGE_COLORS.punch_list },
  { key: 'rehab_complete', label: 'Complete', color: STAGE_COLORS.rehab_complete },
];

export const SELLING_COLUMNS = [
  { key: 'pre_listing', label: 'Pre-Listing', color: STAGE_COLORS.pre_listing },
  { key: 'on_market', label: 'On Market', color: STAGE_COLORS.on_market },
  { key: 'under_contract', label: 'Under Contract', color: STAGE_COLORS.under_contract },
  { key: 'closing', label: 'Closing', color: STAGE_COLORS.closing },
  { key: 'sold', label: 'Sold', color: STAGE_COLORS.sold },
];

// ─── Phase check helpers ─────────────────────────────────────────────────────

export function isRehabPhase(project) {
  return resolveProjectPhase(project) === 'rehab';
}

export function isSellingPhase(project) {
  return resolveProjectPhase(project) === 'selling';
}

// ─── Phase color for badges ──────────────────────────────────────────────────

const PHASE_COLORS = {
  acquisition: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-700 dark:text-violet-300',
    border: 'border-violet-300 dark:border-violet-700',
  },
  rehab: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  selling: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
  },
};

export function getPhaseColor(phase) {
  return PHASE_COLORS[phase] || DEFAULT_COLOR;
}
